import { Link, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { Topbar } from "@/components/parcelio/topbar";
import { BatteryBadge, SignalBadge } from "@/components/parcelio/device-health";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  vehiclesApi,
  trackersApi,
  directoryApi,
  type Vehicle,
  type VehicleType as ApiVehicleType,
  type MaintenanceRecord,
  type DriverDirectoryEntry,
  type Tracker,
} from "@/lib/api";
import { useAuth } from "@/stores/auth";
import { maintenanceKindLabel } from "@/lib/maintenance-data";
import {
  ArrowLeft,
  Bike,
  Car,
  Truck,
  Gauge,
  Wrench,
  Fuel,
  Zap,
  Plus,
  CalendarDays,
  MapPinned,
  ShieldCheck,
  Satellite,
  Check,
  ChevronsUpDown,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { LoadingBlock, Spinner } from "@/components/ui/spinner";

function typeIconFor(name: string | undefined) {
  const n = (name ?? "").toLowerCase();
  if (n.includes("bike") || n.includes("moto")) return Bike;
  if (n.includes("car")) return Car;
  return Truck;
}

function kindLabel(kind: string) {
  return (maintenanceKindLabel as Record<string, string>)[kind] ?? kind;
}

function TrackerPicker({
  value,
  onChange,
  trackers,
  currentVehicleId,
}: {
  value: string;
  onChange: (imei: string) => void;
  trackers: Tracker[];
  currentVehicleId: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const filtered = trackers.filter((t) => {
    const s = query.toLowerCase();
    return (
      t.device_name.toLowerCase().includes(s) ||
      t.imei.includes(s) ||
      (t.vehicle_plate?.toLowerCase().includes(s) ?? false)
    );
  });
  const selected = trackers.find((t) => t.imei === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal">
          {selected ? (
            <span className="truncate">
              {selected.device_name} <span className="font-mono text-xs text-muted-foreground">({selected.imei})</span>
            </span>
          ) : value ? (
            <span className="truncate font-mono text-xs">{value}</span>
          ) : (
            "Select device"
          )}
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search device, IMEI, plate…" value={query} onValueChange={setQuery} />
          <CommandList>
            <CommandEmpty>No device found.</CommandEmpty>
            <CommandGroup>
              {filtered.map((t) => {
                const inUseElsewhere = !!t.vehicle_id && t.vehicle_id !== currentVehicleId;
                return (
                  <CommandItem
                    key={t.id}
                    value={t.imei}
                    disabled={inUseElsewhere}
                    onSelect={() => {
                      if (inUseElsewhere) return;
                      onChange(t.imei);
                      setOpen(false);
                    }}
                    className={cn(inUseElsewhere && "opacity-50")}
                  >
                    <Check className={cn("h-4 w-4", value === t.imei ? "opacity-100" : "opacity-0")} />
                    <span className="flex-1 truncate">
                      {t.device_name} <span className="font-mono text-xs text-muted-foreground">({t.imei})</span>
                    </span>
                    {inUseElsewhere ? (
                      <span className="ml-2 shrink-0 rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                        {t.vehicle_plate}
                      </span>
                    ) : null}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

type DerivedStatus = "Scheduled" | "Overdue" | "Completed";

function deriveStatus(r: MaintenanceRecord): DerivedStatus {
  if (r.completed_at) return "Completed";
  const scheduled = new Date(r.scheduled_for).getTime();
  if (!Number.isNaN(scheduled) && scheduled < Date.now()) return "Overdue";
  return "Scheduled";
}

function statusTone(s: DerivedStatus) {
  return {
    Scheduled: "bg-info/10 text-info border-info/20",
    Overdue: "bg-destructive/10 text-destructive border-destructive/20",
    Completed: "bg-success/10 text-success border-success/20",
  }[s];
}

export default function VehicleDetailPage() {
  const { vehicleId } = useParams();
  const token = useAuth((s) => s.session?.accessToken);
  // Tracker hardware assignment is admin/superadmin-only server-side
  // (require_admin_claims on set_device) — hide the control for everyone
  // else rather than let them click into a 403.
  const isAdmin = useAuth(
    (s) => s.session?.kind === "staff" && (s.session.roles.includes("admin") || s.session.roles.includes("superadmin")),
  );
  const isDriverOnly = useAuth(
    (s) => s.session?.kind === "staff" && s.session.roles.length > 0 && s.session.roles.every((r) => r === "driver"),
  );

  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [types, setTypes] = useState<ApiVehicleType[]>([]);
  const [drivers, setDrivers] = useState<DriverDirectoryEntry[]>([]);
  const [records, setRecords] = useState<MaintenanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    kind: "oil_change",
    scheduledFor: "",
    notes: "",
  });
  const [deviceOpen, setDeviceOpen] = useState(false);
  const [deviceIdInput, setDeviceIdInput] = useState("");
  const [savingDevice, setSavingDevice] = useState(false);
  const [trackers, setTrackers] = useState<Tracker[]>([]);
  const [trackersLoading, setTrackersLoading] = useState(false);

  useEffect(() => {
    if (!vehicleId) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [v, typeList, driverList, maint] = await Promise.all([
          vehiclesApi.get(vehicleId!, token),
          vehiclesApi.listTypes(),
          isDriverOnly ? Promise.resolve([]) : directoryApi.drivers(token),
          vehiclesApi.listMaintenance(vehicleId!, token),
        ]);
        if (cancelled) return;
        setVehicle(v);
        setTypes(typeList);
        setDrivers(driverList);
        setRecords(maint);
      } catch (err) {
        if (!cancelled) {
          setNotFound(true);
          toast.error(err instanceof Error ? err.message : "Failed to load vehicle");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [vehicleId, token]);

  const upcoming = useMemo(
    () => records.filter((r) => deriveStatus(r) !== "Completed"),
    [records],
  );
  const history = useMemo(
    () => records.filter((r) => deriveStatus(r) === "Completed"),
    [records],
  );

  const addRecord = async () => {
    if (!vehicle || !form.scheduledFor) {
      toast.error("Scheduled date is required");
      return;
    }
    setSaving(true);
    try {
      const created = await vehiclesApi.createMaintenance(
        vehicle.id,
        { scheduled_for: form.scheduledFor, kind: form.kind, notes: form.notes || undefined },
        token,
      );
      setRecords((cur) => [created, ...cur]);
      toast.success("Maintenance scheduled");
      setOpen(false);
      setForm({ kind: "oil_change", scheduledFor: "", notes: "" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to schedule maintenance");
    } finally {
      setSaving(false);
    }
  };

  const openDeviceDialog = () => {
    setDeviceIdInput(vehicle?.device_id ?? "");
    setDeviceOpen(true);
    setTrackersLoading(true);
    trackersApi
      .list(token)
      .then(setTrackers)
      .catch(() => toast.error("Failed to load devices"))
      .finally(() => setTrackersLoading(false));
  };

  const saveDevice = async () => {
    if (!vehicle || !deviceIdInput.trim()) return toast.error("Device IMEI required");
    setSavingDevice(true);
    try {
      const res = await vehiclesApi.setDevice(vehicle.id, deviceIdInput.trim(), token);
      setVehicle((cur) => (cur ? { ...cur, device_id: res.device_id } : cur));
      toast.success("GPS tracker linked");
      setDeviceOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to link tracker");
    } finally {
      setSavingDevice(false);
    }
  };

  // NOTE: the backend has no PATCH endpoint for maintenance records yet, so
  // "Complete" only updates local UI state and is not persisted server-side.
  const markCompleted = (id: string) => {
    setRecords((cur) =>
      cur.map((r) => (r.id === id ? { ...r, completed_at: new Date().toISOString() } : r)),
    );
    toast.success("Marked completed (local only — no backend endpoint yet)");
  };

  if (loading) {
    return <LoadingBlock label="Loading vehicle…" />;
  }

  if (notFound || !vehicle) {
    return (
      <div className="p-10 text-center text-sm text-muted-foreground">
        Vehicle not found. <Link to="/app/vehicles" className="text-primary underline">Back to fleet</Link>
      </div>
    );
  }

  const typeInfo = types.find((t) => t.id === vehicle.type_id);
  const Icon = typeIconFor(typeInfo?.name);
  const driver = vehicle.current_driver_id ? drivers.find((d) => d.id === vehicle.current_driver_id) : undefined;
  const odometer = vehicle.odometer_km ?? 0;
  const nextDueKm = vehicle.next_service_km != null ? vehicle.next_service_km - odometer : null;
  const title = [vehicle.make, vehicle.model].filter(Boolean).join(" ") || vehicle.plate;

  return (
    <>
      <Topbar
        title={title}
        subtitle={`${vehicle.plate} · ${vehicle.year ?? "—"}`}
        actions={
          <div className="flex gap-2">
            {isDriverOnly ? null : (
              <Button variant="outline" size="sm" asChild>
                <Link to="/app/vehicles"><ArrowLeft className="mr-1.5 h-4 w-4" /> Fleet</Link>
              </Button>
            )}
            {isDriverOnly ? null : (
              <Button variant="outline" size="sm" asChild>
                <Link to="/app/vehicles/assignments">Assignments</Link>
              </Button>
            )}
            <Button size="sm" onClick={() => setOpen(true)}>
              <Plus className="mr-1.5 h-4 w-4" /> Schedule service
            </Button>
          </div>
        }
      />

      <div className="flex-1 space-y-4 p-4 md:p-6">
        <div className="surface-card p-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-primary-soft text-primary">
                <Icon className="h-8 w-8" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{title}</h2>
                <div className="mt-0.5 font-mono text-sm text-muted-foreground">{vehicle.plate}</div>
                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                  <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 capitalize"><Truck className="h-3 w-3" /> {typeInfo?.name ?? vehicle.type_id}</span>
                  <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5">
                    {vehicle.fuel_type === "electric" ? <Zap className="h-3 w-3" /> : <Fuel className="h-3 w-3" />}
                    <span className="capitalize">{vehicle.fuel_type ?? "—"}</span>
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5"><ShieldCheck className="h-3 w-3" /> Capacity {vehicle.capacity_kg ?? "—"} kg</span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 rounded-lg bg-muted/40 p-3 text-center md:min-w-[280px]">
              <div>
                <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground"><Gauge className="h-3 w-3" /> Odometer</div>
                <div className="mt-1 text-lg font-bold">{vehicle.odometer_km != null ? `${vehicle.odometer_km.toLocaleString()} km` : "—"}</div>
              </div>
              <div>
                <div className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground"><Wrench className="h-3 w-3" /> Next service</div>
                <div className={cn("mt-1 text-lg font-bold", nextDueKm != null && nextDueKm <= 1500 && "text-warning-foreground")}>
                  {nextDueKm == null ? "—" : nextDueKm > 0 ? `${nextDueKm.toLocaleString()} km` : "Due"}
                </div>
              </div>
            </div>
          </div>

          {isDriverOnly ? null : (
            <div className="mt-4 flex items-center justify-between rounded-lg border bg-surface p-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">
                  {driver ? driver.name.slice(0, 2).toUpperCase() : "—"}
                </div>
                <div>
                  <div className="text-sm font-medium">{driver ? driver.name : "Unassigned"}</div>
                  <div className="text-xs text-muted-foreground">
                    {driver ? <><MapPinned className="mr-1 inline h-3 w-3" />{driver.last_location ?? "Location unavailable"}</> : "No driver linked to this vehicle."}
                  </div>
                </div>
              </div>
              <Button variant="outline" size="sm" asChild>
                <Link to="/app/vehicles/assignments">Manage assignment</Link>
              </Button>
            </div>
          )}
        </div>

        {isDriverOnly ? null : (
        <div className="surface-card">
          <div className="flex items-center justify-between border-b p-4">
            <div>
              <h3 className="font-semibold">GPS tracker</h3>
              <p className="text-xs text-muted-foreground">Generic tracker hardware pings this vehicle's location.</p>
            </div>
            <div className="flex gap-2">
              {vehicle.device_id ? (
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/app/vehicles/${vehicle.id}/track`}>
                    <MapPinned className="mr-1.5 h-4 w-4" />
                    Track live
                  </Link>
                </Button>
              ) : (
                <Button variant="outline" size="sm" disabled title="Link a tracker first">
                  <MapPinned className="mr-1.5 h-4 w-4" />
                  Track live
                </Button>
              )}
              {isAdmin ? (
                <Button variant="outline" size="sm" onClick={openDeviceDialog}>
                  <Satellite className="mr-1.5 h-4 w-4" />
                  {vehicle.device_id ? "Change tracker" : "Link tracker"}
                </Button>
              ) : null}
            </div>
          </div>
          <div className="grid min-w-0 grid-cols-2 gap-3 p-4 text-sm md:grid-cols-6">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Device ID</div>
              <div className="mt-0.5 truncate font-mono">{vehicle.device_id ?? "Not provisioned"}</div>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Battery</div>
              <div className="mt-0.5"><BatteryBadge level={vehicle.last_battery_level} /></div>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Signal</div>
              <div className="mt-0.5"><SignalBadge level={vehicle.last_gsm_signal} /></div>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Last position</div>
              <div className="mt-0.5 truncate">
                {vehicle.last_lat != null && vehicle.last_lng != null
                  ? `${vehicle.last_lat.toFixed(4)}, ${vehicle.last_lng.toFixed(4)}`
                  : "—"}
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Last ping</div>
              <div className="mt-0.5 truncate">{vehicle.last_ping_at ? new Date(vehicle.last_ping_at).toLocaleString() : "—"}</div>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Last checkin</div>
              <div className="mt-0.5 truncate">{vehicle.last_status_at ? new Date(vehicle.last_status_at).toLocaleString() : "—"}</div>
            </div>
          </div>
        </div>
        )}

        <div className="surface-card">
          <div className="flex items-center justify-between border-b p-4">
            <div>
              <h3 className="font-semibold">Maintenance schedule</h3>
              <p className="text-xs text-muted-foreground">Upcoming and overdue work orders.</p>
            </div>
            <span className="text-xs text-muted-foreground">{upcoming.length} open</span>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Work order</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Scheduled</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {upcoming.map((r) => {
                const status = deriveStatus(r);
                return (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-xs">{r.id}</TableCell>
                    <TableCell>{kindLabel(r.kind)}</TableCell>
                    <TableCell>{r.scheduled_for}</TableCell>
                    <TableCell className="max-w-[220px] truncate text-muted-foreground">{r.notes ?? "—"}</TableCell>
                    <TableCell>
                      <span className={cn("inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium", statusTone(status))}>
                        {status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" onClick={() => markCompleted(r.id)}>Complete</Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {upcoming.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                    No upcoming work. This vehicle is up to date.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </div>

        <div className="surface-card">
          <div className="flex items-center justify-between border-b p-4">
            <div>
              <h3 className="font-semibold">Service history</h3>
              <p className="text-xs text-muted-foreground">Completed work orders.</p>
            </div>
            <span className="text-xs text-muted-foreground">{history.length} records</span>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.completed_at ?? r.scheduled_for}</TableCell>
                  <TableCell>{kindLabel(r.kind)}</TableCell>
                  <TableCell className="max-w-[280px] truncate text-muted-foreground">{r.notes ?? "—"}</TableCell>
                </TableRow>
              ))}
              {history.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="py-6 text-center text-sm text-muted-foreground">
                    No completed service records yet.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Schedule maintenance</DialogTitle>
            <DialogDescription>Add a new work order for {vehicle.plate}.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div>
              <Label>Type</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(maintenanceKindLabel).map(([k, label]) => (
                    <SelectItem key={k} value={k}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="sched">Scheduled for</Label>
              <Input id="sched" type="date" value={form.scheduledFor} onChange={(e) => setForm({ ...form, scheduledFor: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="note">Notes</Label>
              <Input id="note" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={addRecord} disabled={saving}>
              {saving ? <Spinner className="mr-1.5" /> : null}
              Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deviceOpen} onOpenChange={setDeviceOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Link GPS tracker</DialogTitle>
            <DialogDescription>
              Enter the IMEI printed on the tracker device. Auth is IMEI-whitelist only — no
              token needed, so make sure this ID is unique per device.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Device</Label>
            <div className="mt-1">
              {trackersLoading ? (
                <LoadingBlock label="Loading devices…" />
              ) : (
                <TrackerPicker
                  value={deviceIdInput}
                  onChange={setDeviceIdInput}
                  trackers={trackers}
                  currentVehicleId={vehicle.id}
                />
              )}
            </div>
          </div>
          <div className="rounded-lg border bg-muted/40 p-3 font-mono text-xs text-muted-foreground">
            GET/POST /gps/ping?imei={deviceIdInput || "{imei}"}&lat=&#123;lat&#125;&lon=&#123;lon&#125;
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeviceOpen(false)}>Cancel</Button>
            <Button onClick={saveDevice} disabled={savingDevice}>
              {savingDevice ? <Spinner className="mr-1.5" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
